import {
  AltType,
  NotificationChannel,
  NotificationConfig,
  ReceiverType,
} from '@/types/notification'
import { Ref, reactive, ref } from 'vue'

import Calendar from '@/models/calendar'
import Email from '@/models/email'
import { formatHTMLForTipTap } from '@gohighlevel/ghl-text-editor'
import { Editor } from '@tiptap/vue-3'
import { getDefaultEmailSubjectAndBody, getDefaultWhatsAppTemplateLabel } from '../utils/default-notification-template'
import AppState, { getWhatsAppTemplates } from './app'

const receiverTypes: string[] = Object.values(ReceiverType)

export const getReceiverTypesForChannel = (channel: string): string[] => {
  const channelStr = channel?.toString()

  if (channel === NotificationChannel.SMS) {
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.PHONE_NUMBERS]
  } else if (channel === NotificationChannel.EMAIL) {
    // Ensure correct order: contact, assigned user, guest, additional emails
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.GUEST, ReceiverType.EMAILS]
  } else if (channelStr === 'whatsapp') {
    // For WhatsApp, only support contact, assigned user, and additional phone numbers
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.PHONE_NUMBERS]
  } else {
    // For in-app notifications: contact, assigned user, guest
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.GUEST]
  }
}

/**
 * Sort notifications array by receiver type according to the desired order for the channel
 * This ensures consistent ordering regardless of backend response order
 */
export const sortNotificationsByReceiverType = (
  notifications: NotificationConfig[],
  channel: string
): NotificationConfig[] => {
  const desiredOrder = getReceiverTypesForChannel(channel)

  return notifications.sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.receiverType)
    const indexB = desiredOrder.indexOf(b.receiverType)

    // If receiver type not found in desired order, put it at the end
    const orderA = indexA === -1 ? desiredOrder.length : indexA
    const orderB = indexB === -1 ? desiredOrder.length : indexB

    return orderA - orderB
  })
}

export const eventNotificationState = reactive({
  calendarId: '',
  isEventCalendar: false,
  isCustomFormSelected: false,
  calendar: {} as Calendar | null,
  isLoading: false,
  saving: false,
  disableSave: false,
  showEmailTemplateRefresh: false,
  eventNotificationData: [],
  eventNotifications: [] as NotificationConfig[],
  smsNotifications: [] as NotificationConfig[],
  whatsappNotifications: [] as NotificationConfig[],
  accordions: [] as {
    id: string
    isOpen: boolean
    isChecked: boolean
  }[],
  lastOpenedAccordion: null as number | null,
  inAppNotification: {} as NotificationConfig,
  enableNotification: false,
  contact: {},
  guest: {},
  assignedUser: {},
  additionalEmails: {},
})
export const resetState = () => {
  eventNotificationState.eventNotifications = []
  eventNotificationState.smsNotifications = []
  eventNotificationState.whatsappNotifications = []
  eventNotificationState.contact = {}
  eventNotificationState.guest = {}
  eventNotificationState.assignedUser = {}
  eventNotificationState.additionalEmails = {}
}
export class EventNotification {
  actionTemplate: Ref<Email | undefined>
  state = reactive({} as NotificationConfig)
  editor: Editor
  toAddress: Ref<string | undefined>
  testPhoneNumber: Ref<string | undefined>
  open = ref(false)
  checked = ref(false)
  constructor(props: NotificationConfig) {
    this.actionTemplate = ref<Email>(new Email())
    this.editor = [] as any
    this.toAddress = ref<string | undefined>(undefined)
    this.testPhoneNumber = ref<string | undefined>(undefined)
    this.open = ref(false)
    this.checked = ref(false)
    this.state = reactive({
      _id: props._id,
      altType: props.altType,
      altId: props.altId,
      receiverType: props.receiverType,
      additionalEmailIds: props.additionalEmailIds,
      additionalPhoneNumbers: props.additionalPhoneNumbers,
      channel: props.channel,
      notificationType: props.notificationType,
      isActive: props.isActive,
      templateId: props.templateId,
      body: formatHTMLForTipTap(props.body || ''),
      subject: props.subject,
      beforeTime: props.beforeTime || [],
      afterTime: props.afterTime || [],
      selectedUsers: props.selectedUsers,
      fromAddress: props.fromAddress || '',
      fromName: props.fromName || '',
    })
  }
}

export const updateStateOfNotification = (
  id: string,
  state: Partial<NotificationConfig>
) => {
  const index = eventNotificationState.eventNotifications.findIndex(
    notification => notification._id === id
  )
  if (index !== -1) {
    eventNotificationState.eventNotifications[index] = {
      ...eventNotificationState.eventNotifications[index],
      ...state,
    }
  } else {
    console.warn(`Notification with id ${id} not found.`)
  }
}

export const getDefaultNotifications = (
  notification: Partial<NotificationConfig>
) => {
  const defaultNotifications = [] as NotificationConfig[]
  const channel = notification.channel || NotificationChannel.EMAIL
  const channelReceiverTypes = getReceiverTypesForChannel(channel as string)

  channelReceiverTypes.forEach(receiverType => {
    if (
      !(
        eventNotificationState.isEventCalendar &&
        receiverType === ReceiverType.ASSIGNED_USER
      )
    ) {
      notification.receiverType = receiverType as any
      notification.channel = channel
      const defaultNotification = getDefaultNotification(notification)
      if (defaultNotification) {
        defaultNotifications.push(defaultNotification as NotificationConfig)
      }
    }
  })
  return defaultNotifications
}

// WhatsApp-specific async version
export const getDefaultWhatsAppNotifications = async (
  notification: Partial<NotificationConfig>
) => {
  const defaultNotifications = [] as NotificationConfig[]
  const channel = notification.channel || NotificationChannel.WHATSAPP
  const channelReceiverTypes = getReceiverTypesForChannel(channel as string)

  for (const receiverType of channelReceiverTypes) {
    if (
      !(
        eventNotificationState.isEventCalendar &&
        receiverType === ReceiverType.ASSIGNED_USER
      )
    ) {
      notification.receiverType = receiverType as any
      notification.channel = channel
      const defaultNotification = await getDefaultWhatsAppNotification(notification)
      if (defaultNotification) {
        defaultNotifications.push(defaultNotification as NotificationConfig)
      }
    }
  }
  return defaultNotifications
}

export const getDefaultNotification = (
  notification: Partial<NotificationConfig>
): NotificationConfig | null => {
  const defaultTemplate = getDefaultEmailSubjectAndBody(
    notification,
    eventNotificationState.isCustomFormSelected
  )
  if (
    notification.channel === NotificationChannel.EMAIL &&
    ((notification.receiverType === ReceiverType.GUEST &&
      !AppState.editCalendarValidationData.enableGuests) ||
      (eventNotificationState.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER))
  ) {
    return null
  }
  // For non-WhatsApp channels, templateId remains empty
  let defaultTemplateId = ''

  const defaultNotification = {
    _id: notification._id || '',
    altType: AltType.CALENDAR,
    altId: notification.altId || '',
    receiverType: notification.receiverType,
    channel: notification.channel || NotificationChannel.EMAIL,
    notificationType: notification.notificationType,
    isActive: true,
    templateId: defaultTemplateId,
    body: defaultTemplate.html || defaultTemplate.smsText || '',
    subject: defaultTemplate.subject,
    beforeTime: [],
    afterTime: [],
    selectedUsers: [],
    deleted: false,
  } as NotificationConfig

  // Add channel-specific fields
  const channelStr = notification.channel?.toString()
  if (channelStr === 'sms' || channelStr === 'whatsapp') {
    defaultNotification.additionalPhoneNumbers = []
  } else {
    defaultNotification.additionalEmailIds = []
  }

  return defaultNotification
}

// WhatsApp-specific async version that resolves template IDs
export const getDefaultWhatsAppNotification = async (
  notification: Partial<NotificationConfig>
): Promise<NotificationConfig | null> => {
  const defaultTemplate = getDefaultEmailSubjectAndBody(
    notification,
    eventNotificationState.isCustomFormSelected
  )
  if (
    notification.channel === NotificationChannel.EMAIL &&
    ((notification.receiverType === ReceiverType.GUEST &&
      !AppState.editCalendarValidationData.enableGuests) ||
      (eventNotificationState.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER))
  ) {
    return null
  }

  // Get default template ID for WhatsApp notifications
  let defaultTemplateId = ''
  if (notification.channel?.toString() === 'whatsapp') {
    const defaultTemplateLabel = getDefaultWhatsAppTemplateLabel(
      notification.notificationType || '',
      notification.receiverType || ''
    )

    if (defaultTemplateLabel) {
      try {
        // Fetch WhatsApp templates to get the actual template ID
        const whatsappTemplates = await getWhatsAppTemplates()
        const defaultTemplate = whatsappTemplates.find(template =>
          template.label &&
          template.label.toLowerCase() === defaultTemplateLabel.toLowerCase()
        )

        if (defaultTemplate) {
          defaultTemplateId = defaultTemplate.value as string
        }
      } catch (error) {
        console.warn('Error fetching WhatsApp templates for default notification:', error)
      }
    }
  }

  const defaultNotification = {
    _id: notification._id || '',
    altType: AltType.CALENDAR,
    altId: notification.altId || '',
    receiverType: notification.receiverType,
    channel: notification.channel || NotificationChannel.EMAIL,
    notificationType: notification.notificationType,
    isActive: true,
    templateId: defaultTemplateId,
    body: defaultTemplate.html || defaultTemplate.smsText || '',
    subject: defaultTemplate.subject,
    beforeTime: [],
    afterTime: [],
    selectedUsers: [],
    deleted: false,
  } as NotificationConfig

  // Add channel-specific fields
  const channelStr = notification.channel?.toString()
  if (channelStr === 'sms' || channelStr === 'whatsapp') {
    defaultNotification.additionalPhoneNumbers = []
  } else {
    defaultNotification.additionalEmailIds = []
  }

  return defaultNotification
}

export function updateNotificationState(data: NotificationConfig[]) {
  data.forEach(notification => {
    notification.body = formatHTMLForTipTap(notification.body)

    if (notification.channel?.toString() === 'sms') {
      const index = eventNotificationState.smsNotifications.findIndex(
        n => n.receiverType === notification.receiverType
      )
      if (index !== -1) {
        eventNotificationState.smsNotifications[index] = notification
      } else {
        eventNotificationState.smsNotifications.push(notification)
      }
    } else if (notification.channel?.toString() === 'whatsapp') {
      const index = eventNotificationState.whatsappNotifications.findIndex(
        n => n.receiverType === notification.receiverType
      )
      if (index !== -1) {
        eventNotificationState.whatsappNotifications[index] = notification
      } else {
        eventNotificationState.whatsappNotifications.push(notification)
      }
    } else {
      const index = eventNotificationState.eventNotifications.findIndex(
        n => n.receiverType === notification.receiverType
      )
      if (index !== -1) {
        eventNotificationState.eventNotifications[index] = notification
      }
    }

    if (eventNotificationState[notification.receiverType]?.state) {
      // Preserve existing beforeTime and afterTime arrays if they exist and notification arrays are empty
      const existingState = eventNotificationState[notification.receiverType].state
      const preservedBeforeTime = (notification.beforeTime?.length === 0 && existingState.beforeTime?.length > 0)
        ? existingState.beforeTime
        : notification.beforeTime
      const preservedAfterTime = (notification.afterTime?.length === 0 && existingState.afterTime?.length > 0)
        ? existingState.afterTime
        : notification.afterTime

      Object.assign(eventNotificationState[notification.receiverType].state, notification, {
        beforeTime: preservedBeforeTime,
        afterTime: preservedAfterTime,
      })
    }
  })

  // Sort all notification arrays to maintain consistent ordering after updates
  eventNotificationState.eventNotifications = sortNotificationsByReceiverType(
    eventNotificationState.eventNotifications,
    NotificationChannel.EMAIL
  )
  eventNotificationState.smsNotifications = sortNotificationsByReceiverType(
    eventNotificationState.smsNotifications,
    NotificationChannel.SMS
  )
  eventNotificationState.whatsappNotifications = sortNotificationsByReceiverType(
    eventNotificationState.whatsappNotifications,
    'whatsapp'
  )
}

export function getEventNotificationState(tab: string) {
  // check if all the email notification are enabled
  // all are disabled
  let isNotificationsEnabled = true
  if (tab === 'inApp') {
    const isAvailableInDb = !!eventNotificationState.inAppNotification._id
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    return !eventNotificationState.inAppNotification.deleted
  }
  if (tab === 'email') {
    const isAvailableInDb = eventNotificationState.eventNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.eventNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.eventNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.eventNotifications
      }
    }
  }
  if (tab === 'sms') {
    const isAvailableInDb = eventNotificationState.smsNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.smsNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.smsNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.smsNotifications
      }
    }
  }
  if (tab === 'whatsapp') {
    const isAvailableInDb = eventNotificationState.whatsappNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.whatsappNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.whatsappNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.whatsappNotifications
      }
    }
  }
  return isNotificationsEnabled
}
